import {
  faPencil,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { API_URL } from "../../config"; // Import API_URL from config
import LoadingSpinner from "../../components/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";


const WebsiteReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [addPage, setAddPage] = useState("");
  const [deleteId, setDeleteId] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // 🔎 Search Filters
  const [subscriberFilter, setSubscriberFilter] = useState("");
  const [descriptionFilter, setDescriptionFilter] = useState("");

  // 📑 Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // 🏷️ Review Type Toggle
  const [reviewType, setReviewType] = useState("streetgains"); // "streetgains" (0) or "streetfolio" (1)

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem("authToken");
  };

  // 🧩 Fetch Data from API
  const fetchReviews = async (pageNum = 1) => {
    setLoading(true);
    setError("");
    try {
      const productApp = reviewType === "streetfolio" ? 1 : 0;
      const token = getToken();

      const response = await fetch(
        `${API_URL}testimonials/list?page=${pageNum}&limit=${limit}&product_app=${productApp}`,
        {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        const testimonials = result.data.testimonials || [];
        setReviews(testimonials);
        setTotal(result.data.total || testimonials.length);
        setPageCount(Math.ceil((result.data.total || testimonials.length) / limit));
      } else {
        throw new Error(result.message || 'Failed to fetch testimonials');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching reviews:', err);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  // Fetch reviews when component mounts or when review type/page changes
  useEffect(() => {
    fetchReviews(page);
  }, [reviewType, page]);

  // Update add page when review type changes
  useEffect(() => {
    setAddPage(`/settings/websitereviews/add/${reviewType}`);
  }, [reviewType]);

  useEffect(() => {
    if (!reviews.length) return;

    setIsFiltering(true);

    const timer = setTimeout(() => {
      setIsFiltering(false);
    }, 300); // smooth UX

    return () => clearTimeout(timer);
  }, [subscriberFilter, descriptionFilter]);


  // 🔎 Filter logic with review type
  const filteredReviews = useMemo(() => {
    return reviews.filter((review) => {
      if (
        subscriberFilter &&
        !(review.reviewby || "")
          .toLowerCase()
          .includes(subscriberFilter.toLowerCase())
      )
        return false;

      if (
        descriptionFilter &&
        !(review.description || "")
          .toLowerCase()
          .includes(descriptionFilter.toLowerCase())
      )
        return false;

      return true;
    });
  }, [reviews, subscriberFilter, descriptionFilter]);

  // 📑 Pagination handler
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
      fetchReviews(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  // ✏️ Edit & 🗑 Delete
  const handleEdit = (id) => {
    // window.location.href = `/settings/websitereviews/edit/${reviewType}/${id}`;
    navigate(`/WebsiteReviews/edit/${id}`);
  };

  const handleDelete = (id) => {
    setDeleteId(id);

    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this review!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        confirmDelete();
      }
    });
  };

  const confirmDelete = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${API_URL}testimonials/${deleteId}`,
        {
          method: 'DELETE',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        Swal.fire("Deleted!", "Your review has been deleted.", "success");
        // Refresh the list after successful deletion
        fetchReviews(page);
      } else {
        throw new Error(result.message || 'Failed to delete testimonial');
      }
    } catch (err) {
      console.error('Error deleting review:', err);
      Swal.fire(
        "Error",
        err.message || "Failed to delete the review.",
        "error"
      );
    } finally {
      closeModal();
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalContent("");
  };

  // Toggle between review types
  const toggleReviewType = (type) => {
    setReviewType(type);
    setSubscriberFilter("");
    setDescriptionFilter("");
    setPage(1);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setSubscriberFilter("");
      setDescriptionFilter("");
    };
    window.addEventListener('clearWebsiteReviewsFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearWebsiteReviewsFilterEvent', handleClearFilters);
    };
  }, []);

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">Manage Website Reviews</h4>
          </div>
          <div className="col-md-6 text-right">
            {((reviewType === "streetgains" &&
              hasPermission("streetgains_reviews", "add")) ||
              (reviewType === "streetfolio" &&
                hasPermission("streetfolio_reviews", "add"))) && (
                <Link
                  to={`/WebsiteReviews/add`}
                  className="btn btn-default btn-sm"
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Review
                </Link>
              )}
          </div>
        </div>

        {/* Review Type Toggle */}
        <div className="panel panel-default with-rounded-corner with-shadow mb-3">
          <div className="panel-body">
            <div className="row">
              <div className="col-md-12">
                <label className="me-3">Select Review Type:</label>
                <div className="btn-group" role="group">
                  {/* {hasPermission("streetgains_reviews", "view") && ( */}
                  <button
                    type="button"
                    className={`btn ${reviewType === "streetgains" ? "btn-primary" : "btn-default"
                      }`}
                    onClick={() => toggleReviewType("streetgains")}
                  >
                    Streetgains Reviews
                  </button>
                  {/* )} */}
                  {/* {hasPermission("streetfolio_reviews", "view") && ( */}
                  <button
                    type="button"
                    className={`btn ${reviewType === "streetfolio" ? "btn-primary" : "btn-default"
                      }`}
                    onClick={() => toggleReviewType("streetfolio")}
                  >
                    Streetfolio Reviews
                  </button>
                  {/* )} */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button type="button" className="btn-close" onClick={() => setError("")}></button>
          </div>
        )}

        <div className="panel panel-default with-rounded-corner with-shadow">
          {/* Pagination Controls */}
          {reviews.length > 0 && (
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <div className="panel-heading border-0">
                <h4 className="panel-title m-0">
                  {reviewType === "streetfolio" ? "Streetfolio" : "Streetgains"} Reviews
                </h4>
              </div>
              <div className="d-inline-block">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1 || loading}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount || loading}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          )}

          {/* Loading Spinner */}
          {/* {loading && (
            <div className="text-center p-4">
              <LoadingSpinner />
            </div>
          )} */}

          {/* Table */}
          {!loading && (
            <div className="table-responsive user-table-container">
              <style>
                {`
                  .user-table-container {
                    position: relative;
                    max-height: 71vh;
                    overflow-y: auto;
                    overflow-x: auto;
                   }

                  /* First header row (titles) */
                  .user-table-container table thead tr:nth-child(1) th {
                    position: sticky;
                    top: -1px;
                    z-index: 30;
                    background: #f8f9fa;
                  }

                 /* Second header row (search inputs) */
                 .user-table-container table thead tr:nth-child(2) th {
                   position: sticky;
                   top: 25px; /* adjust based on header height */
                   z-index: 25;
                   background: #fff;
                  }
               `}
              </style>
              <table className="table m-b-0">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>#</th>
                    <th>Subscriber Name</th>
                    <th>Description</th>
                    <th>Created Date</th>
                    {/* <th>Status</th> */}
                    <th style={{ width: "80px" }}>Action</th>
                  </tr>

                  {/* Search Filter Row */}
                  <tr>
                    <th></th>
                    <th>
                      <input
                        type="text"
                        placeholder="Search name..."
                        className="form-control input-sm"
                        style={{
                          width: "150px",
                          fontSize: "12px",
                          padding: "3px 6px",
                          height: "28px",
                        }}
                        value={subscriberFilter}
                        onChange={(e) => setSubscriberFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        placeholder="Search description..."
                        className="form-control input-sm"
                        style={{
                          width: "200px",
                          fontSize: "12px",
                          padding: "3px 6px",
                          height: "28px",
                        }}
                        value={descriptionFilter}
                        onChange={(e) => setDescriptionFilter(e.target.value)}
                      />
                    </th>
                    <th></th>
                    {/* <th></th> */}
                    <th>
                      <button
                        className="btn btn-default btn-sm"
                        onClick={() => {
                          setSubscriberFilter("");
                          setDescriptionFilter("");
                        }}
                      >
                        Clear
                      </button>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {(loading || isFiltering || isInitialLoad) ? (
                    Array.from({ length: limit }).map((_, i) => (
                      <tr key={i}>
                        <td style={{ textAlign: "center" }}>
                          <div className="skeleton-box" />
                        </td>
                        <td>
                          <div className="skeleton-box" />
                        </td>
                        <td>
                          <div className="skeleton-box" />
                        </td>
                        <td>
                          <div className="skeleton-box" />
                        </td>
                        <td>
                          <div className="skeleton-box" />
                        </td>
                      </tr>
                    ))
                  ) : filteredReviews.length > 0 ? (
                    filteredReviews.map((review, index) => (
                      <tr key={review.id}>
                        <td
                          style={{ textAlign: "center", verticalAlign: "middle" }}
                        >
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td>{review.reviewby}</td>
                        <td>
                          <div style={{
                            maxWidth: "300px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}>
                            {review.description}
                          </div>
                        </td>
                        <td>{formatDate(review.created_on)}</td>
                        {/* <td>
                          <span 
                            className={`badge ${
                              review.isActive === 1 ? "bg-success" : "bg-danger"
                            }`}
                          >
                            {review.isActive === 1 ? "Active" : "Inactive"}
                          </span>
                        </td> */}
                        <td style={{ width: "80px" }}>
                          <div className="d-flex gap-2">
                            {((reviewType === "streetgains" &&
                              hasPermission("streetgains_reviews", "edit")) ||
                              (reviewType === "streetfolio" &&
                                hasPermission("streetfolio_reviews", "edit"))) && (
                                <button
                                  onClick={() => handleEdit(review.id)}
                                  className="btn btn-warning btn-xs"
                                  title="Edit Review"
                                >
                                  <FontAwesomeIcon icon={faPencil} />
                                </button>
                              )}
                            {((reviewType === "streetgains" &&
                              hasPermission("streetgains_reviews", "delete")) ||
                              (reviewType === "streetfolio" &&
                                hasPermission("streetfolio_reviews", "delete"))) && (
                                <button
                                  onClick={() => handleDelete(review.id)}
                                  className="btn btn-danger btn-xs"
                                  title="Delete Review"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: "center" }}>
                        {reviews.length === 0 ? "No reviews found" : "No matching records found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showModal && (
          <div id="myModal" className="modal show" style={{ display: 'block' }}>
            <div className="modal-dialog panel panel-default panel-border top">
              <div className="modal-content p-3">{modalContent}</div>
            </div>
          </div>
        )}
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default WebsiteReviews;